import { Colors, Container, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { TipGridContent } from './components/TipGridContent';
import { TipIconListContent } from './components/TipIconListContent';
import { TipSectionShell } from './components/TipSectionShell';
import { TipStackedContent } from './components/TipStackedContent';

import { useAmelioTranslations } from '@/hooks';

interface MeetingTipsAccordionProps {
    dataTestId?: string;
}

export const MeetingTipsAccordion = ({
    dataTestId = 'meeting-tips-accordion',
}: MeetingTipsAccordionProps) => {
    const { t } = useAmelioTranslations();
    const [isExpanded, setIsExpanded] = useState(false);
    const [openSection, setOpenSection] = useState<string | null>(null);

    const toggleSection = (key: string) => {
        setOpenSection((current) => (current === key ? null : key));
    };

    return (
        <Container
            p={'lg'}
            mb={'md'}
            border={'white'}
            borderRadius={'2xl'}
            background={'translucent'}
            className={'!p-4 md:!p-11'}
            dataTestId={dataTestId}
        >
            <button
                type={'button'}
                onClick={() => setIsExpanded((current) => !current)}
                className={'flex w-full cursor-pointer items-center justify-between'}
                data-testid={'meeting-tips-toggle'}
            >
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'lucide-lightbulb'}
                        size={'md'}
                        color={Colors.PRIMARY}
                        className={'!h-6 !w-6'}
                    />

                    <Text
                        size={'large'}
                        weight={'semibold'}
                        color={Colors.BASE_600}
                        className={'!text-lg md:!text-2xl'}
                    >
                        {t('postReviewMeeting.tips.title')}
                    </Text>
                </div>

                <Icon
                    name={'arrow-arrow-down'}
                    size={'sm'}
                    color={Colors.BASE_400}
                    rotate={isExpanded ? 180 : 0}
                />
            </button>

            {isExpanded && (
                <>
                    <div className={'mt-4 space-y-3'}>
                        <TipSectionShell
                            title={t('postReviewMeeting.tips.before.title')}
                            number={1}
                            numberColor={'orange'}
                            isOpen={openSection === 'before'}
                            onToggle={() => toggleSection('before')}
                        >
                            <TipStackedContent
                                items={[
                                    {
                                        title: t('postReviewMeeting.tips.before.item1Title'),
                                        description: t(
                                            'postReviewMeeting.tips.before.item1Description',
                                        ),
                                    },
                                    {
                                        title: t('postReviewMeeting.tips.before.item2Title'),
                                        description: t(
                                            'postReviewMeeting.tips.before.item2Description',
                                        ),
                                    },
                                ]}
                            />
                        </TipSectionShell>

                        <TipSectionShell
                            title={t('postReviewMeeting.tips.during.title')}
                            number={2}
                            numberColor={'teal'}
                            isOpen={openSection === 'during'}
                            onToggle={() => toggleSection('during')}
                        >
                            <TipGridContent
                                items={[
                                    {
                                        icon: 'lucide-ear',
                                        title: t('postReviewMeeting.tips.during.item1Title'),
                                        description: t(
                                            'postReviewMeeting.tips.during.item1Description',
                                        ),
                                    },
                                    {
                                        icon: 'lucide-sparkles',
                                        title: t('postReviewMeeting.tips.during.item2Title'),
                                        description: t(
                                            'postReviewMeeting.tips.during.item2Description',
                                        ),
                                    },
                                    {
                                        icon: 'lucide-circle-help',
                                        title: t('postReviewMeeting.tips.during.item3Title'),
                                        description: t(
                                            'postReviewMeeting.tips.during.item3Description',
                                        ),
                                    },
                                    {
                                        icon: 'lucide-heart',
                                        title: t('postReviewMeeting.tips.during.item4Title'),
                                        description: t(
                                            'postReviewMeeting.tips.during.item4Description',
                                        ),
                                    },
                                ]}
                            />
                        </TipSectionShell>

                        <TipSectionShell
                            title={t('postReviewMeeting.tips.after.title')}
                            number={3}
                            numberColor={'purple'}
                            isOpen={openSection === 'after'}
                            onToggle={() => toggleSection('after')}
                        >
                            <TipIconListContent
                                items={[
                                    {
                                        icon: 'lucide-eye',
                                        description: t(
                                            'postReviewMeeting.tips.after.item1Description',
                                        ),
                                    },
                                    {
                                        icon: 'lucide-message-circle',
                                        description: t(
                                            'postReviewMeeting.tips.after.item2Description',
                                        ),
                                    },
                                    {
                                        icon: 'lucide-refresh-ccw',
                                        description: t(
                                            'postReviewMeeting.tips.after.item3Description',
                                        ),
                                    },
                                    {
                                        icon: 'lucide-arrow-left',
                                        description: t(
                                            'postReviewMeeting.tips.after.item4Description',
                                        ),
                                    },
                                ]}
                            />
                        </TipSectionShell>
                    </div>

                    <div
                        className={
                            'to-[#f7f0ff ] !mt-6 flex items-center gap-3 rounded-lg border border-[#8751ec33] bg-gradient-to-r from-[#eadbfa] !p-4 md:!mt-12 md:gap-4 md:!p-9'
                        }
                        data-testid={'meeting-tips-pro-tip-banner'}
                    >
                        <span className={'text-4xl'}>✨</span>

                        <Text className={'!text-base leading-relaxed !text-[#8751ec] md:!text-lg'}>
                            <span className={'font-semibold'}>
                                {t('postReviewMeeting.tips.proTipLabel')}
                            </span>{' '}
                            {t('postReviewMeeting.tips.proTip')}
                        </Text>
                    </div>
                </>
            )}
        </Container>
    );
};

export default MeetingTipsAccordion;
